import { format, subDays } from 'date-fns'
import { useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useAccount } from '@/api/hooks/use-account'
import { useAnalytics } from '@/api/hooks/use-analytics'
import { type DateRange, DateRangePicker } from '@/components/ui/date-range-picker'
import { MetricCard } from '@/components/ui/metric-card'
import { formatNumber, formatPercent } from '@/lib/format'
import { PlanCard } from '@/pages/overview-plan-card'

const BOUNCE_RATE_DANGER_THRESHOLD = 3
const COMPLAINT_RATE_DANGER_THRESHOLD = 0.05

export function OverviewPage(): React.JSX.Element {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: new Date(),
  })

  const accountQuery = useAccount()

  const { data, isLoading } = useAnalytics({
    from: dateRange.from,
    to: dateRange.to,
    granularity: 'day',
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-lg font-medium text-text-primary">No data yet</p>
        <p className="mt-1 text-sm text-text-secondary">Send your first email to see analytics.</p>
      </div>
    )
  }

  const { totals } = data

  // Analytics returns rates as ratios (0-1), convert to percentages for display
  const deliveryPct = totals.delivery_rate * 100
  const bouncePct = totals.bounce_rate * 100
  const complaintPct = totals.complaint_rate * 100
  const openPct = totals.open_rate * 100
  const clickPct = totals.click_rate * 100

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">Overview</h1>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      {accountQuery.data && (
        <PlanCard
          tier={accountQuery.data.account.tier}
          marketingTier={accountQuery.data.account.marketing_tier ?? 'free'}
          emailsSent={accountQuery.data.account.emails_sent_this_period}
          marketingEmailsSent={accountQuery.data.account.marketing_emails_sent_this_period ?? 0}
          contactCount={accountQuery.data.account.contact_count ?? 0}
          periodEnd={accountQuery.data.account.period_end}
        />
      )}

      {/* Metric cards row */}
      <p className="text-xs text-text-tertiary">
        Showing {format(dateRange.from, 'MMM d')} – {format(dateRange.to, 'MMM d, yyyy')}
      </p>
      <div className="-mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <MetricCard label="Sent" value={formatNumber(totals.sent)} />
        <MetricCard
          label="Delivered"
          value={formatNumber(totals.delivered)}
          sub={formatPercent(deliveryPct)}
          variant="success"
        />
        <MetricCard
          label="Bounced"
          value={formatNumber(totals.bounced)}
          sub={formatPercent(bouncePct)}
          variant={bouncePct > BOUNCE_RATE_DANGER_THRESHOLD ? 'danger' : 'default'}
        />
        <MetricCard
          label="Complained"
          value={formatNumber(totals.complained)}
          sub={formatPercent(complaintPct)}
          variant={complaintPct > COMPLAINT_RATE_DANGER_THRESHOLD ? 'danger' : 'default'}
        />
        <MetricCard
          label="Opens"
          value={formatNumber(totals.opened)}
          sub={formatPercent(openPct)}
        />
        <MetricCard
          label="Clicks"
          value={formatNumber(totals.clicked)}
          sub={formatPercent(clickPct)}
        />
      </div>

      {/* Send Volume Area Chart */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <div className="mb-4">
          <h2 className="text-sm font-medium text-text-primary">Send Volume</h2>
          <p className="text-xs text-text-tertiary">Sent vs Delivered over the selected period</p>
        </div>
        {data.timeseries.length === 0 ? (
          <p className="py-12 text-center text-sm text-text-tertiary">
            No email data yet — send your first email to see analytics here.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.timeseries}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(v: string) => format(new Date(v), 'MMM d')}
                stroke="var(--color-text-tertiary)"
                fontSize={12}
              />
              <YAxis stroke="var(--color-text-tertiary)" fontSize={12} />
              <Tooltip
                labelFormatter={(v: string) => format(new Date(v), 'MMM d, yyyy')}
                contentStyle={{
                  backgroundColor: 'var(--color-surface-0)',
                  border: '1px solid var(--color-surface-3)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Area
                type="monotone"
                dataKey="sent"
                stroke="var(--color-brand-500)"
                fill="var(--color-brand-100)"
                strokeWidth={2}
                name="Sent"
              />
              <Area
                type="monotone"
                dataKey="delivered"
                stroke="var(--color-success)"
                fill="var(--color-success)"
                fillOpacity={0.15}
                strokeWidth={2}
                name="Delivered"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

    </div>
  )
}
